import React, { useState, useEffect, useCallback, useMemo } from 'react';
import locationService from '../../services/locationService';
import MasterPageWrapper from './common/MasterPageWrapper';
import FormSection from './common/FormSection';
import ConfirmModal from './components/ConfirmModal';
import CommonPagination from '../../components/common/CommonPagination';
import usePagination from '../../hooks/usePagination';
import './LocationMasterPage.css';

const TABS = [
  { id: 'countries', label: 'Country Master', entityName: 'Country', icon: '🌍' },
  { id: 'states', label: 'State Master', entityName: 'State', icon: '🏛️' },
  { id: 'cities', label: 'City Master', entityName: 'City', icon: '🏙️' },
  { id: 'pincodes', label: 'PIN Code Master', entityName: 'PIN Code', icon: '📮' },
  { id: 'addresses', label: 'Address Master', entityName: 'Address', icon: '📍' },
];

export const LocationMasterPage = ({ showToast }) => {
  const [activeTab, setActiveTab] = useState('countries');
  const [viewMode, setViewMode] = useState('form'); // Form-First experience
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Dropdown reference lists for cascading filters & selectors
  const [allCountries, setAllCountries] = useState([]);
  const [allStates, setAllStates] = useState([]);
  const [allCities, setAllCities] = useState([]);

  // Filter states for list view
  const [filterCountryId, setFilterCountryId] = useState('');
  const [filterStateId, setFilterStateId] = useState('');
  const [filterCityId, setFilterCityId] = useState('');

  // Table Data lists
  const [countriesList, setCountriesList] = useState([]);
  const [statesList, setStatesList] = useState([]);
  const [citiesList, setCitiesList] = useState([]);
  const [pinCodesList, setPinCodesList] = useState([]);
  const [addressesList, setAddressesList] = useState([]);

  // Form State
  const [modalMode, setModalMode] = useState('create'); // 'create' | 'edit'
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    countryName: '',
    countryCode: '',
    isoCode2: '',
    phoneCode: '+91',
    isActive: true
  });
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Status toggle confirmation
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [statusUpdating, setStatusUpdating] = useState(false);

  const currentTab = useMemo(() => TABS.find(t => t.id === activeTab) || TABS[0], [activeTab]);

  // 1. Initial Reference Data Fetch
  const loadReferenceCountries = useCallback(async () => {
    try {
      const res = await locationService.getCountries();
      if (res.success && res.data) {
        setAllCountries(res.data);
      }
    } catch (err) {
      console.error('Failed to load countries reference:', err);
    }
  }, []);

  const loadReferenceStates = useCallback(async (cId) => {
    try {
      const res = await locationService.getStates({ countryId: cId || undefined });
      if (res.success && res.data) {
        setAllStates(res.data);
      }
    } catch (err) {
      console.error('Failed to load states reference:', err);
    }
  }, []);

  const loadReferenceCities = useCallback(async (sId) => {
    try {
      const res = await locationService.getCities({ stateId: sId || undefined });
      if (res.success && res.data) {
        setAllCities(res.data);
      }
    } catch (err) {
      console.error('Failed to load cities reference:', err);
    }
  }, []);

  useEffect(() => {
    loadReferenceCountries();
    loadReferenceStates();
    loadReferenceCities();
  }, [loadReferenceCountries, loadReferenceStates, loadReferenceCities]);

  // 2. Fetch Active Tab List Data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      if (activeTab === 'countries') {
        const res = await locationService.getCountries();
        if (res.success) setCountriesList(res.data || []);
      } else if (activeTab === 'states') {
        const res = await locationService.getStates({
          countryId: filterCountryId ? parseInt(filterCountryId, 10) : undefined,
          search: searchQuery.trim() || undefined,
        });
        if (res.success) setStatesList(res.data || []);
      } else if (activeTab === 'cities') {
        const res = await locationService.getCities({
          stateId: filterStateId ? parseInt(filterStateId, 10) : undefined,
          search: searchQuery.trim() || undefined,
        });
        if (res.success) setCitiesList(res.data || []);
      } else if (activeTab === 'pincodes') {
        const res = await locationService.getPinCodes({
          cityId: filterCityId ? parseInt(filterCityId, 10) : undefined,
          search: searchQuery.trim() || undefined,
        });
        if (res.success) setPinCodesList(res.data || []);
      } else if (activeTab === 'addresses') {
        const res = await locationService.getAddresses({
          cityId: filterCityId ? parseInt(filterCityId, 10) : undefined,
          search: searchQuery.trim() || undefined,
        });
        if (res.success) setAddressesList(res.data || []);
      }
    } catch (err) {
      console.error(`Failed to load ${activeTab}:`, err);
      showToast?.(`Failed to load ${activeTab} data.`, 'error');
    } finally {
      setLoading(false);
    }
  }, [activeTab, filterCountryId, filterStateId, filterCityId, searchQuery, showToast]);

  useEffect(() => {
    if (viewMode === 'list') {
      fetchData();
    }
  }, [viewMode, fetchData]);

  // When filterCountryId changes, reload states dropdown & reset child filters
  const handleFilterCountryChange = (e) => {
    const val = e.target.value;
    setFilterCountryId(val);
    setFilterStateId('');
    setFilterCityId('');
    loadReferenceStates(val);
  };

  // When filterStateId changes, reload cities dropdown & reset city filter
  const handleFilterStateChange = (e) => {
    const val = e.target.value;
    setFilterStateId(val);
    setFilterCityId('');
    loadReferenceCities(val);
  };

  // Open / Reset Form for Create
  const handleOpenCreate = useCallback((tabId = activeTab) => {
    setModalMode('create');
    setEditingItem(null);
    setFormErrors({});

    if (tabId === 'countries') {
      setFormData({ countryName: '', countryCode: '', isoCode2: '', phoneCode: '+91', isActive: true });
    } else if (tabId === 'states') {
      setFormData({ countryId: filterCountryId || (allCountries[0]?.countryId ?? 1), stateName: '', stateCode: '', isActive: true });
    } else if (tabId === 'cities') {
      setFormData({
        countryId: filterCountryId || (allCountries[0]?.countryId ?? 1),
        stateId: filterStateId || (allStates[0]?.stateId ?? ''),
        cityName: '',
        isActive: true,
      });
    } else if (tabId === 'pincodes') {
      setFormData({
        countryId: filterCountryId || (allCountries[0]?.countryId ?? 1),
        stateId: filterStateId || (allStates[0]?.stateId ?? ''),
        cityId: filterCityId || (allCities[0]?.cityId ?? ''),
        pinCode: '',
        areaName: '',
        isActive: true,
      });
    } else if (tabId === 'addresses') {
      setFormData({
        countryId: filterCountryId || (allCountries[0]?.countryId ?? 1),
        stateId: filterStateId || (allStates[0]?.stateId ?? ''),
        cityId: filterCityId || (allCities[0]?.cityId ?? ''),
        pinCodeId: null,
        addressLine1: '',
        addressLine2: '',
        area: '',
        landmark: '',
        addressType: 'Residential',
        isActive: true,
      });
    }
    setViewMode('form');
  }, [activeTab, filterCountryId, filterStateId, filterCityId, allCountries, allStates, allCities]);

  // Tab change handler — always opens Form first for that tab!
  const handleTabChange = (newTabId) => {
    setActiveTab(newTabId);
    setSearchQuery('');
    handleOpenCreate(newTabId);
  };

  // Open Form for Edit
  const handleOpenEdit = (item) => {
    setModalMode('edit');
    setEditingItem(item);
    setFormErrors({});

    if (activeTab === 'countries') {
      setFormData({
        countryName: item.countryName,
        countryCode: item.countryCode,
        isoCode2: item.isoCode2,
        phoneCode: item.phoneCode || '',
        isActive: item.isActive,
      });
    } else if (activeTab === 'states') {
      setFormData({
        countryId: item.countryId,
        stateName: item.stateName,
        stateCode: item.stateCode || '',
        isActive: item.isActive,
      });
    } else if (activeTab === 'cities') {
      setFormData({
        countryId: item.countryId,
        stateId: item.stateId,
        cityName: item.cityName,
        isActive: item.isActive,
      });
    } else if (activeTab === 'pincodes') {
      setFormData({
        countryId: item.countryId,
        stateId: item.stateId,
        cityId: item.cityId,
        pinCode: item.pinCode,
        areaName: item.areaName || '',
        isActive: item.isActive,
      });
    } else if (activeTab === 'addresses') {
      setFormData({
        countryId: item.countryId,
        stateId: item.stateId,
        cityId: item.cityId,
        pinCodeId: item.pinCodeId || null,
        addressLine1: item.addressLine1,
        addressLine2: item.addressLine2 || '',
        area: item.area || '',
        landmark: item.landmark || '',
        addressType: item.addressType || 'Residential',
        isActive: item.isActive,
      });
    }
    setViewMode('form');
  };

  // Handle Form Change
  const handleFormChange = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'countryId') {
        next.stateId = '';
        next.cityId = '';
        loadReferenceStates(value);
      } else if (field === 'stateId') {
        next.cityId = '';
        loadReferenceCities(value);
      }
      return next;
    });
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  // Submit Form
  const handleSaveForm = async (e) => {
    e.preventDefault();
    const errors = {};

    if (activeTab === 'countries') {
      if (!formData.countryName?.trim()) errors.countryName = 'Country Name is required.';
      if (!formData.countryCode?.trim()) errors.countryCode = 'Country Code is required.';
    } else if (activeTab === 'states') {
      if (!formData.countryId) errors.countryId = 'Country is required.';
      if (!formData.stateName?.trim()) errors.stateName = 'State Name is required.';
    } else if (activeTab === 'cities') {
      if (!formData.stateId) errors.stateId = 'State is required.';
      if (!formData.cityName?.trim()) errors.cityName = 'City Name is required.';
    } else if (activeTab === 'pincodes') {
      if (!formData.cityId) errors.cityId = 'City is required.';
      if (!formData.pinCode?.trim()) errors.pinCode = 'PIN Code is required.';
      else if (!/^\d{6}$/.test(formData.pinCode.trim())) errors.pinCode = 'PIN Code must be 6 digits.';
    } else if (activeTab === 'addresses') {
      if (!formData.cityId) errors.cityId = 'City is required.';
      if (!formData.addressLine1?.trim()) errors.addressLine1 = 'Address Line 1 is required.';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setSaving(true);
    try {
      let res;
      if (activeTab === 'countries') {
        res = modalMode === 'create'
          ? await locationService.createCountry(formData)
          : await locationService.updateCountry(editingItem.countryId, formData);
      } else if (activeTab === 'states') {
        res = modalMode === 'create'
          ? await locationService.createState(formData)
          : await locationService.updateState(editingItem.stateId, formData);
      } else if (activeTab === 'cities') {
        res = modalMode === 'create'
          ? await locationService.createCity(formData)
          : await locationService.updateCity(editingItem.cityId, formData);
      } else if (activeTab === 'pincodes') {
        res = modalMode === 'create'
          ? await locationService.createPinCode(formData)
          : await locationService.updatePinCode(editingItem.pinCodeId, formData);
      } else if (activeTab === 'addresses') {
        res = modalMode === 'create'
          ? await locationService.createAddress(formData)
          : await locationService.updateAddress(editingItem.addressId, formData);
      }

      if (res && (res.success || res.data)) {
        showToast?.(`${currentTab.entityName} ${modalMode === 'create' ? 'created' : 'updated'} successfully.`, 'success');
        setViewMode('list');
        fetchData();
        loadReferenceCountries();
        loadReferenceStates();
        loadReferenceCities();
      } else {
        showToast?.(res?.message || 'Failed to save record.', 'error');
      }
    } catch (err) {
      console.error('Error saving location master:', err);
      showToast?.(err.response?.data?.message || 'Error occurred while saving.', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Toggle Active Status
  const handleToggleStatus = async (item) => {
    setStatusUpdating(true);
    try {
      let res;
      if (activeTab === 'countries') res = await locationService.toggleCountryStatus(item.countryId);
      else if (activeTab === 'states') res = await locationService.toggleStateStatus(item.stateId);
      else if (activeTab === 'cities') res = await locationService.toggleCityStatus(item.cityId);
      else if (activeTab === 'pincodes') res = await locationService.togglePinCodeStatus(item.pinCodeId);
      else if (activeTab === 'addresses') res = await locationService.toggleAddressStatus(item.addressId);

      if (res && (res.success || res.data)) {
        showToast?.('Status updated successfully.', 'success');
        fetchData();
      }
    } catch (err) {
      console.error('Error toggling status:', err);
      showToast?.('Failed to update status.', 'error');
    } finally {
      setStatusUpdating(false);
      setConfirmTarget(null);
    }
  };

  // Filtered in-memory records based on search query
  const filteredData = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) {
      if (activeTab === 'countries') return countriesList;
      if (activeTab === 'states') return statesList;
      if (activeTab === 'cities') return citiesList;
      if (activeTab === 'pincodes') return pinCodesList;
      if (activeTab === 'addresses') return addressesList;
      return [];
    }

    if (activeTab === 'countries') {
      return countriesList.filter(
        (c) => c.countryName?.toLowerCase().includes(q) || c.countryCode?.toLowerCase().includes(q)
      );
    }
    if (activeTab === 'states') {
      return statesList.filter(
        (s) => s.stateName?.toLowerCase().includes(q) || s.stateCode?.toLowerCase().includes(q) || s.countryName?.toLowerCase().includes(q)
      );
    }
    if (activeTab === 'cities') {
      return citiesList.filter(
        (ci) => ci.cityName?.toLowerCase().includes(q) || ci.stateName?.toLowerCase().includes(q) || ci.countryName?.toLowerCase().includes(q)
      );
    }
    if (activeTab === 'pincodes') {
      return pinCodesList.filter(
        (p) => p.pinCode?.includes(q) || p.areaName?.toLowerCase().includes(q) || p.cityName?.toLowerCase().includes(q)
      );
    }
    if (activeTab === 'addresses') {
      return addressesList.filter(
        (a) => a.addressLine1?.toLowerCase().includes(q) || a.area?.toLowerCase().includes(q) || a.cityName?.toLowerCase().includes(q) || a.pinCode?.includes(q)
      );
    }
    return [];
  }, [activeTab, searchQuery, countriesList, statesList, citiesList, pinCodesList, addressesList]);

  // Centralized Pagination hook for location lists
  const locationPagination = usePagination({
    items: filteredData,
    resetDeps: [activeTab, searchQuery, filterCountryId, filterStateId, filterCityId],
  });

  return (
    <MasterPageWrapper
      masterTitle={viewMode === 'form' ? (modalMode === 'create' ? `Create ${currentTab.entityName}` : `Update ${currentTab.entityName}`) : currentTab.label}
      masterName={currentTab.entityName}
      viewMode={viewMode}
      onToggleView={(mode) => {
        if (mode === 'form' && modalMode !== 'edit') handleOpenCreate();
        else setViewMode(mode);
      }}
    >
      <div className="loc-master-container">
        {/* Navigation Tabs Bar */}
        <div className="loc-tabs-bar" style={{ marginBottom: '1.25rem' }}>
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`loc-tab-item ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => handleTabChange(tab.id)}
            >
              <span className="tab-icon">{tab.icon}</span>
              <span className="tab-label">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* ─── FULL-SCREEN FORM VIEW (FORM-FIRST EXPERIENCE) ─── */}
        {viewMode === 'form' && (
          <div className="glass-card fade-in">
            <form onSubmit={handleSaveForm} noValidate>
              <FormSection
                title={`01. ${currentTab.entityName} Identification & Configuration`}
                iconColor="#3b82f6"
                icon={
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="2" y1="12" x2="22" y2="12" />
                    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                  </svg>
                }
              >
                {/* ── Country Form ── */}
                {activeTab === 'countries' && (
                  <div className="form-grid">
                    <div className="form-group">
                      <label className="form-label">
                        Country Name <span className="form-req">*</span>
                      </label>
                      <input
                        type="text"
                        className={`form-input ${formErrors.countryName ? 'border-danger' : ''}`}
                        value={formData.countryName || ''}
                        onChange={(e) => handleFormChange('countryName', e.target.value)}
                        placeholder="e.g. India, United States"
                        required
                      />
                      {formErrors.countryName && <span className="error-message">{formErrors.countryName}</span>}
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        Country Code (ISO-3) <span className="form-req">*</span>
                      </label>
                      <input
                        type="text"
                        className={`form-input ${formErrors.countryCode ? 'border-danger' : ''}`}
                        maxLength={3}
                        style={{ textTransform: 'uppercase', fontFamily: 'monospace' }}
                        value={formData.countryCode || ''}
                        onChange={(e) => handleFormChange('countryCode', e.target.value.toUpperCase())}
                        placeholder="e.g. IND, USA"
                        required
                      />
                      {formErrors.countryCode && <span className="error-message">{formErrors.countryCode}</span>}
                    </div>

                    <div className="form-group">
                      <label className="form-label">ISO-2 Code</label>
                      <input
                        type="text"
                        className="form-input"
                        maxLength={2}
                        style={{ textTransform: 'uppercase', fontFamily: 'monospace' }}
                        value={formData.isoCode2 || ''}
                        onChange={(e) => handleFormChange('isoCode2', e.target.value.toUpperCase())}
                        placeholder="e.g. IN, US"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Dial / Phone Code</label>
                      <input
                        type="text"
                        className="form-input"
                        value={formData.phoneCode || ''}
                        onChange={(e) => handleFormChange('phoneCode', e.target.value)}
                        placeholder="e.g. +91, +1"
                      />
                    </div>
                  </div>
                )}

                {/* ── State Form ── */}
                {activeTab === 'states' && (
                  <div className="form-grid">
                    <div className="form-group">
                      <label className="form-label">
                        Parent Country <span className="form-req">*</span>
                      </label>
                      <select
                        className={`form-select ${formErrors.countryId ? 'border-danger' : ''}`}
                        value={formData.countryId || ''}
                        onChange={(e) => handleFormChange('countryId', parseInt(e.target.value, 10))}
                        required
                      >
                        <option value="">-- Select Country --</option>
                        {allCountries.map((c) => (
                          <option key={c.countryId} value={c.countryId}>
                            {c.countryName} ({c.countryCode})
                          </option>
                        ))}
                      </select>
                      {formErrors.countryId && <span className="error-message">{formErrors.countryId}</span>}
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        State / Province Name <span className="form-req">*</span>
                      </label>
                      <input
                        type="text"
                        className={`form-input ${formErrors.stateName ? 'border-danger' : ''}`}
                        value={formData.stateName || ''}
                        onChange={(e) => handleFormChange('stateName', e.target.value)}
                        placeholder="e.g. Haryana, California"
                        required
                      />
                      {formErrors.stateName && <span className="error-message">{formErrors.stateName}</span>}
                    </div>

                    <div className="form-group">
                      <label className="form-label">State Code</label>
                      <input
                        type="text"
                        className="form-input"
                        maxLength={5}
                        style={{ textTransform: 'uppercase', fontFamily: 'monospace' }}
                        value={formData.stateCode || ''}
                        onChange={(e) => handleFormChange('stateCode', e.target.value.toUpperCase())}
                        placeholder="e.g. HR, CA"
                      />
                    </div>
                  </div>
                )}

                {/* ── City Form ── */}
                {activeTab === 'cities' && (
                  <div className="form-grid">
                    <div className="form-group">
                      <label className="form-label">
                        Parent Country <span className="form-req">*</span>
                      </label>
                      <select
                        className="form-select"
                        value={formData.countryId || ''}
                        onChange={(e) => handleFormChange('countryId', parseInt(e.target.value, 10))}
                      >
                        <option value="">-- Select Country --</option>
                        {allCountries.map((c) => (
                          <option key={c.countryId} value={c.countryId}>
                            {c.countryName}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        Parent State <span className="form-req">*</span>
                      </label>
                      <select
                        className={`form-select ${formErrors.stateId ? 'border-danger' : ''}`}
                        value={formData.stateId || ''}
                        onChange={(e) => handleFormChange('stateId', parseInt(e.target.value, 10))}
                        disabled={!formData.countryId}
                        required
                      >
                        <option value="">-- Select State --</option>
                        {allStates
                          .filter((s) => !formData.countryId || s.countryId === parseInt(formData.countryId, 10))
                          .map((s) => (
                            <option key={s.stateId} value={s.stateId}>
                              {s.stateName}
                            </option>
                          ))}
                      </select>
                      {formErrors.stateId && <span className="error-message">{formErrors.stateId}</span>}
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        City / District Name <span className="form-req">*</span>
                      </label>
                      <input
                        type="text"
                        className={`form-input ${formErrors.cityName ? 'border-danger' : ''}`}
                        value={formData.cityName || ''}
                        onChange={(e) => handleFormChange('cityName', e.target.value)}
                        placeholder="e.g. Gurugram, Mumbai"
                        required
                      />
                      {formErrors.cityName && <span className="error-message">{formErrors.cityName}</span>}
                    </div>
                  </div>
                )}

                {/* ── PIN Code Form ── */}
                {activeTab === 'pincodes' && (
                  <div className="form-grid">
                    <div className="form-group">
                      <label className="form-label">Country</label>
                      <select
                        className="form-select"
                        value={formData.countryId || ''}
                        onChange={(e) => handleFormChange('countryId', parseInt(e.target.value, 10))}
                      >
                        <option value="">-- Select Country --</option>
                        {allCountries.map((c) => (
                          <option key={c.countryId} value={c.countryId}>
                            {c.countryName}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label className="form-label">State</label>
                      <select
                        className="form-select"
                        value={formData.stateId || ''}
                        onChange={(e) => handleFormChange('stateId', parseInt(e.target.value, 10))}
                        disabled={!formData.countryId}
                      >
                        <option value="">-- Select State --</option>
                        {allStates
                          .filter((s) => !formData.countryId || s.countryId === parseInt(formData.countryId, 10))
                          .map((s) => (
                            <option key={s.stateId} value={s.stateId}>
                              {s.stateName}
                            </option>
                          ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        City <span className="form-req">*</span>
                      </label>
                      <select
                        className={`form-select ${formErrors.cityId ? 'border-danger' : ''}`}
                        value={formData.cityId || ''}
                        onChange={(e) => handleFormChange('cityId', parseInt(e.target.value, 10))}
                        disabled={!formData.stateId}
                        required
                      >
                        <option value="">-- Select City --</option>
                        {allCities
                          .filter((ci) => !formData.stateId || ci.stateId === parseInt(formData.stateId, 10))
                          .map((ci) => (
                            <option key={ci.cityId} value={ci.cityId}>
                              {ci.cityName}
                            </option>
                          ))}
                      </select>
                      {formErrors.cityId && <span className="error-message">{formErrors.cityId}</span>}
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        Postal PIN Code (6 digits) <span className="form-req">*</span>
                      </label>
                      <input
                        type="text"
                        className={`form-input ${formErrors.pinCode ? 'border-danger' : ''}`}
                        maxLength={6}
                        value={formData.pinCode || ''}
                        onChange={(e) => handleFormChange('pinCode', e.target.value.replace(/\D/g, ''))}
                        placeholder="e.g. 122001"
                        required
                      />
                      {formErrors.pinCode && <span className="error-message">{formErrors.pinCode}</span>}
                    </div>

                    <div className="form-group">
                      <label className="form-label">Area / Locality Name</label>
                      <input
                        type="text"
                        className="form-input"
                        value={formData.areaName || ''}
                        onChange={(e) => handleFormChange('areaName', e.target.value)}
                        placeholder="e.g. DLF Cyber City"
                      />
                    </div>
                  </div>
                )}

                {/* ── Address Form ── */}
                {activeTab === 'addresses' && (
                  <div className="form-grid">
                    <div className="form-group">
                      <label className="form-label">Country</label>
                      <select
                        className="form-select"
                        value={formData.countryId || ''}
                        onChange={(e) => handleFormChange('countryId', parseInt(e.target.value, 10))}
                      >
                        <option value="">-- Select Country --</option>
                        {allCountries.map((c) => (
                          <option key={c.countryId} value={c.countryId}>
                            {c.countryName}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label className="form-label">State</label>
                      <select
                        className="form-select"
                        value={formData.stateId || ''}
                        onChange={(e) => handleFormChange('stateId', parseInt(e.target.value, 10))}
                      >
                        <option value="">-- Select State --</option>
                        {allStates.map((s) => (
                          <option key={s.stateId} value={s.stateId}>
                            {s.stateName}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        City <span className="form-req">*</span>
                      </label>
                      <select
                        className={`form-select ${formErrors.cityId ? 'border-danger' : ''}`}
                        value={formData.cityId || ''}
                        onChange={(e) => handleFormChange('cityId', parseInt(e.target.value, 10))}
                        disabled={!formData.stateId}
                        required
                      >
                        <option value="">-- Select City --</option>
                        {allCities
                          .filter((ci) => !formData.stateId || ci.stateId === parseInt(formData.stateId, 10))
                          .map((ci) => (
                            <option key={ci.cityId} value={ci.cityId}>
                              {ci.cityName}
                            </option>
                          ))}
                      </select>
                      {formErrors.cityId && <span className="error-message">{formErrors.cityId}</span>}
                    </div>

                    <div className="form-group">
                      <label className="form-label">Address Type</label>
                      <select
                        className="form-select"
                        value={formData.addressType || 'Residential'}
                        onChange={(e) => handleFormChange('addressType', e.target.value)}
                      >
                        <option value="Residential">Residential</option>
                        <option value="Clinic">Clinic</option>
                        <option value="Collection Hub">Collection Hub</option>
                        <option value="Regional Reference Lab">Regional Reference Lab</option>
                        <option value="Corporate Facility">Corporate Facility</option>
                      </select>
                    </div>

                    <div className="form-group" style={{ gridColumn: 'span 2' }}>
                      <label className="form-label">
                        Address Line 1 (Street / Building) <span className="form-req">*</span>
                      </label>
                      <input
                        type="text"
                        className={`form-input ${formErrors.addressLine1 ? 'border-danger' : ''}`}
                        value={formData.addressLine1 || ''}
                        onChange={(e) => handleFormChange('addressLine1', e.target.value)}
                        placeholder="Plot No. 42, Institutional Area"
                        required
                      />
                      {formErrors.addressLine1 && <span className="error-message">{formErrors.addressLine1}</span>}
                    </div>

                    <div className="form-group">
                      <label className="form-label">Address Line 2 (Suite / Landmark)</label>
                      <input
                        type="text"
                        className="form-input"
                        value={formData.addressLine2 || ''}
                        onChange={(e) => handleFormChange('addressLine2', e.target.value)}
                        placeholder="Suite / Floor / Landmark"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Area / Locality</label>
                      <input
                        type="text"
                        className="form-input"
                        value={formData.area || ''}
                        onChange={(e) => handleFormChange('area', e.target.value)}
                        placeholder="e.g. Sector 44"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Landmark</label>
                      <input
                        type="text"
                        className="form-input"
                        value={formData.landmark || ''}
                        onChange={(e) => handleFormChange('landmark', e.target.value)}
                        placeholder="e.g. Near Metro Station"
                      />
                    </div>
                  </div>
                )}

                {/* Status Switch */}
                <div style={{ marginTop: '1.25rem' }}>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.isActive ?? true}
                      onChange={(e) => handleFormChange('isActive', e.target.checked)}
                    />
                    <span><strong>Active Status</strong> (Visible in clinical master records and cascading location selectors)</span>
                  </label>
                </div>
              </FormSection>

              {/* Form Footer Actions */}
              <div className="form-actions-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setViewMode('list')}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={saving}
                  style={{ minWidth: '150px' }}
                >
                  {saving ? 'Saving...' : (modalMode === 'create' ? `Save ${currentTab.entityName}` : `Update ${currentTab.entityName}`)}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* ─── FULL-SCREEN GRID / LIST VIEW ─── */}
        {viewMode === 'list' && (
          <div className="glass-card fade-in" style={{ padding: '1.5rem' }}>
            {/* Toolbar Filters & Search */}
            <div className="filter-bar">
              <div className="filter-bar-left">
                <div style={{ position: 'relative', width: '280px', minWidth: '220px' }}>
                  <input
                    type="text"
                    className="form-input"
                    placeholder={`Search ${currentTab.label}...`}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery('')}
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: 'var(--text-muted)'
                      }}
                    >
                      ✕
                    </button>
                  )}
                </div>

                {/* Country Filter (For State, City, PIN, Address tabs) */}
                {activeTab !== 'countries' && (
                  <select
                    className="form-select"
                    style={{ width: '180px' }}
                    value={filterCountryId}
                    onChange={handleFilterCountryChange}
                  >
                    <option value="">All Countries</option>
                    {allCountries.map((c) => (
                      <option key={c.countryId} value={c.countryId}>
                        {c.countryName}
                      </option>
                    ))}
                  </select>
                )}

                {/* State Filter (For City, PIN, Address tabs) */}
                {['cities', 'pincodes', 'addresses'].includes(activeTab) && (
                  <select
                    className="form-select"
                    style={{ width: '180px' }}
                    value={filterStateId}
                    onChange={handleFilterStateChange}
                  >
                    <option value="">All States</option>
                    {allStates
                      .filter((s) => !filterCountryId || s.countryId === parseInt(filterCountryId, 10))
                      .map((s) => (
                        <option key={s.stateId} value={s.stateId}>
                          {s.stateName}
                        </option>
                      ))}
                  </select>
                )}

                {/* City Filter (For PIN, Address tabs) */}
                {['pincodes', 'addresses'].includes(activeTab) && (
                  <select
                    className="form-select"
                    style={{ width: '180px' }}
                    value={filterCityId}
                    onChange={(e) => setFilterCityId(e.target.value)}
                  >
                    <option value="">All Cities</option>
                    {allCities
                      .filter((ci) => !filterStateId || ci.stateId === parseInt(filterStateId, 10))
                      .map((ci) => (
                        <option key={ci.cityId} value={ci.cityId}>
                          {ci.cityName}
                        </option>
                      ))}
                  </select>
                )}
              </div>
            </div>

            {/* Data Table */}
            <div style={{ marginTop: '1rem' }}>
              {loading ? (
                <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                  <div className="spinner" style={{ margin: '0 auto 1rem auto' }}></div>
                  <span>Loading {currentTab.label} records...</span>
                </div>
              ) : locationPagination.totalRecords === 0 ? (
                <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                  <span style={{ fontSize: '2rem', display: 'block', marginBottom: '0.5rem' }}>📂</span>
                  <h4>No {currentTab.label} records found</h4>
                  <p style={{ fontSize: '0.875rem' }}>Try adjusting your search query or cascading location filters.</p>
                </div>
              ) : (
                <>
                  <div className="table-responsive">
                    <table className="table">
                    <thead>
                      {activeTab === 'countries' && (
                        <tr>
                          <th>#</th>
                          <th>Country Name</th>
                          <th>ISO-3 Code</th>
                          <th>ISO-2 Code</th>
                          <th>Dial Code</th>
                          <th>Status</th>
                          <th style={{ textAlign: 'right' }}>Actions</th>
                        </tr>
                      )}
                      {activeTab === 'states' && (
                        <tr>
                          <th>#</th>
                          <th>State Name</th>
                          <th>State Code</th>
                          <th>Country</th>
                          <th>Status</th>
                          <th style={{ textAlign: 'right' }}>Actions</th>
                        </tr>
                      )}
                      {activeTab === 'cities' && (
                        <tr>
                          <th>#</th>
                          <th>City Name</th>
                          <th>State</th>
                          <th>Country</th>
                          <th>Status</th>
                          <th style={{ textAlign: 'right' }}>Actions</th>
                        </tr>
                      )}
                      {activeTab === 'pincodes' && (
                        <tr>
                          <th>#</th>
                          <th>PIN Code</th>
                          <th>Area / Locality</th>
                          <th>City</th>
                          <th>Status</th>
                          <th style={{ textAlign: 'right' }}>Actions</th>
                        </tr>
                      )}
                      {activeTab === 'addresses' && (
                        <tr>
                          <th>#</th>
                          <th>Address Line 1</th>
                          <th>Area</th>
                          <th>City</th>
                          <th>Type</th>
                          <th>Status</th>
                          <th style={{ textAlign: 'right' }}>Actions</th>
                        </tr>
                      )}
                    </thead>
                    <tbody>
                      {locationPagination.paginatedItems.map((item, idx) => {
                        const sNo = (locationPagination.page - 1) * locationPagination.pageSize + idx + 1;
                        return (
                          <tr key={item.countryId || item.stateId || item.cityId || item.pinCodeId || item.addressId}>
                            <td>{sNo}</td>
                            {activeTab === 'countries' && (
                              <>
                                <td><strong>{item.countryName}</strong></td>
                                <td><code>{item.countryCode}</code></td>
                                <td>{item.isoCode2 || '—'}</td>
                                <td>{item.phoneCode || '—'}</td>
                              </>
                            )}
                            {activeTab === 'states' && (
                              <>
                                <td><strong>{item.stateName}</strong></td>
                                <td><code>{item.stateCode || '—'}</code></td>
                                <td>{item.countryName || '—'}</td>
                              </>
                            )}
                            {activeTab === 'cities' && (
                              <>
                                <td><strong>{item.cityName}</strong></td>
                                <td>{item.stateName || '—'}</td>
                                <td>{item.countryName || '—'}</td>
                              </>
                            )}
                            {activeTab === 'pincodes' && (
                              <>
                                <td><strong style={{ fontFamily: 'monospace' }}>{item.pinCode}</strong></td>
                                <td>{item.areaName || '—'}</td>
                                <td>{item.cityName || '—'}</td>
                              </>
                            )}
                            {activeTab === 'addresses' && (
                              <>
                                <td><strong>{item.addressLine1}</strong></td>
                                <td>{item.area || '—'}</td>
                                <td>{item.cityName || '—'}</td>
                                <td><span className="badge badge-secondary">{item.addressType || 'General'}</span></td>
                              </>
                            )}
                            <td>
                              <span className={`badge ${item.isActive ? 'badge-success' : 'badge-danger'}`}>
                                {item.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td style={{ textAlign: 'right' }}>
                              <div style={{ display: 'inline-flex', gap: '0.4rem', justifyContent: 'flex-end' }}>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-secondary"
                                  onClick={() => handleOpenEdit(item)}
                                  title="Edit Record"
                                >
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  className={`btn btn-sm ${item.isActive ? 'btn-danger' : 'btn-secondary'}`}
                                  onClick={() => setConfirmTarget(item)}
                                  title={item.isActive ? 'Deactivate' : 'Activate'}
                                >
                                  {item.isActive ? 'Deactivate' : 'Activate'}
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Centralized Pagination */}
                <CommonPagination
                  page={locationPagination.page}
                  pageSize={locationPagination.pageSize}
                  totalRecords={locationPagination.totalRecords}
                  onPageChange={locationPagination.setPage}
                  onPageSizeChange={locationPagination.setPageSize}
                  loading={loading}
                />
              </>
            )}
            </div>
          </div>
        )}
      </div>

      {/* Confirmation Modal for Toggle Active */}
      <ConfirmModal
        isOpen={Boolean(confirmTarget)}
        onCancel={() => setConfirmTarget(null)}
        onConfirm={() => handleToggleStatus(confirmTarget)}
        loading={statusUpdating}
        title="Confirm Status Change"
        message={`Are you sure you want to ${confirmTarget?.isActive ? 'deactivate' : 'activate'} this ${currentTab.entityName}?`}
      />
    </MasterPageWrapper>
  );
};

export default LocationMasterPage;
